/**
 * Hooks de lectura del dominio Administración (TanStack Query).
 *
 * Datos de servidor -> TanStack Query (`docs/frontend_conventions.md`, Datos): no
 * `useState`+`useEffect`+`fetch`. La red la gobierna `lib/api`.
 *  - Sedes: catálogo global (sin sede en la key).
 *  - Atendidos: acotado por la sede activa (`useAuth().activeSede`), que entra en
 *    la key para cachear por sede. La sede viaja en `X-Sede-Id` (lo inyecta
 *    `lib/api`); aquí solo entra en la key para segmentar el cache.
 */
import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/lib/auth";

import {
  fetchAtendido,
  fetchAtendidos,
  fetchSede,
  fetchSedes,
} from "../api";
import type {
  AtendidoDetail,
  AtendidosList,
  SedeDetail,
  SedesList,
} from "../types";
import {
  atendidoDetailQueryKey,
  atendidosListQueryKey,
  sedeDetailQueryKey,
  sedesListQueryKey,
} from "./queryKeys";

/** Lista de sedes con estadísticas (`GET /sedes`). */
export function useSedes() {
  return useQuery<SedesList>({
    queryKey: sedesListQueryKey(),
    queryFn: fetchSedes,
    staleTime: 15_000,
  });
}

/** Detalle de una sede; deshabilitado mientras `sedeId` sea `null`. */
export function useSede(sedeId: number | null) {
  return useQuery<SedeDetail>({
    queryKey: sedeDetailQueryKey(sedeId ?? 0),
    queryFn: () => fetchSede(sedeId as number),
    enabled: sedeId !== null,
    staleTime: 15_000,
  });
}

/** Lista de atendidos activos de la sede activa (`GET /atendidos`). */
export function useAtendidos() {
  const { activeSede } = useAuth();
  const sedeId = activeSede?.id ?? null;
  return useQuery<AtendidosList>({
    queryKey: atendidosListQueryKey(sedeId),
    queryFn: fetchAtendidos,
    staleTime: 15_000,
  });
}

/** Detalle de un atendido; deshabilitado mientras `atendidoId` sea `null`. */
export function useAtendido(atendidoId: number | null) {
  const { activeSede } = useAuth();
  const sedeId = activeSede?.id ?? null;
  return useQuery<AtendidoDetail>({
    queryKey: atendidoDetailQueryKey(sedeId, atendidoId ?? 0),
    queryFn: () => fetchAtendido(atendidoId as number),
    enabled: atendidoId !== null,
    staleTime: 15_000,
  });
}
